import React, { useState, useCallback } from "react";
import { ArrowLeft, Search, Printer, XCircle, Loader2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import ShiftSummaryCards from "../components/sales/shift/ShiftSummaryCards";
import ShiftTransactionTable from "../components/sales/shift/ShiftTransactionTable";
import ShiftRecapModal from "../components/sales/shift/ShiftRecapModal";
import TransactionDetailModal from "../components/sales/shift/TransactionDetailModal";
import CancelTransactionModal from "../components/sales/shift/CancelTransactionModal";
import CloseShiftModal from "../components/sales/shift/CloseShiftModal";
import ShiftAddMutationModal from "../components/sales/shift/ShiftAddMutationModal";
import ShiftMutationTable from "../components/sales/shift/ShiftMutationTable";
import { useActiveShift } from "../hooks/useShift";
import { useNotification } from "../contexts/NotificationContext";
import { getShiftOrders } from "../api/shiftService";
import { authService } from "../api/authService";

function formatDateTime(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CashierShift() {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [activeTab, setActiveTab] = useState("completed"); // 'completed' | 'cancelled'
  const [showRecapModal, setShowRecapModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showCloseShiftModal, setShowCloseShiftModal] = useState(false);
  const [showAddMutationModal, setShowAddMutationModal] = useState(false);

  const {
    activeShift,
    loading: shiftLoading,
    refreshActiveShift,
  } = useActiveShift();
  const { showSuccess, showError } = useNotification();
  const isAdmin = authService.isAdmin();
  const [tabCounts, setTabCounts] = useState({
    completed: null,
    cancelled: null,
    mutations: null,
  });
  const [cancelTransaction, setCancelTransaction] = useState(null); // transaction for CancelTransactionModal
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const handleCancelSuccess = () => {
    setTableRefreshKey((k) => k + 1);
    showSuccess("Status transaksi berhasil diperbarui.");
  };

  const handleTabLoad = useCallback((tab, total) => {
    setTabCounts((prev) => {
      if (prev[tab] === total) return prev;
      return { ...prev, [tab]: total };
    });
  }, []);

  const handleCloseShiftClick = async () => {
    if (!activeShift?.id) return;
    try {
      const res = await getShiftOrders(activeShift.id, { page: 1, limit: 1, status: "CANCEL_REQUESTED" });
      const cancelReqCount = res?.meta?.total ?? res?.meta?.total_items ?? (Array.isArray(res?.data) ? res.data.length : 0);
      
      if (cancelReqCount > 0) {
        showError("Terdapat permintaan pembatalan transaksi yang belum disetujui. Harap tunggu persetujuan sebelum menutup shift.");
        return;
      }
      
      setShowCloseShiftModal(true);
    } catch (err) {
      console.error(err);
      showError("Gagal memeriksa status transaksi shift.");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 font-sans">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 transition-all duration-300 pointer-events-none md:pointer-events-auto ${isSidebarCollapsed ? "w-16" : "w-48"}`}>
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          onToggle={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
          activePage="penjualan"
        />
      </div>

      {/* Main Content */}
      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
        />

        <main className="flex-1 overflow-y-auto">
          {/* Toolbar */}
          <div className="bg-white ring-1 ring-slate-200/70 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/penjualan")}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors">
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <h1 className="text-xl font-bold text-slate-900 tracking-tight">
                Shift Kasir
              </h1>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari data..."
                  className="pl-9 pr-4 py-2 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                />
              </div>
              <button
                onClick={() => setShowRecapModal(true)}
                className="p-2 bg-white ring-1 ring-slate-200 rounded-lg hover:bg-slate-50 text-slate-600 transition-colors">
                <Printer className="w-5 h-5" />
              </button>
              <button
                onClick={handleCloseShiftClick}
                className="px-4 py-2 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 text-sm shadow-sm shadow-rose-600/20 transition-colors">
                Tutup Kasir
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <ShiftSummaryCards
              shiftId={activeShift?.id}
              activeTab={activeTab}
              refreshKey={tableRefreshKey}
            />

            <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm">
              <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex gap-6">
                  <button
                    onClick={() => setActiveTab("completed")}
                    className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
                      activeTab === "completed"
                        ? "border-teal-600 text-teal-600"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}>
                    Transaksi Selesai
                    {tabCounts.completed !== null && (
                      <span
                        className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                          activeTab === "completed"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-slate-100 text-slate-500"
                        }`}>
                        {tabCounts.completed}
                      </span>
                    )}
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => setActiveTab("cancelled")}
                      className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
                        activeTab === "cancelled"
                          ? "border-teal-600 text-teal-600"
                          : "border-transparent text-slate-500 hover:text-slate-700"
                      }`}>
                      Transaksi Dibatalkan
                      {tabCounts.cancelled !== null && (
                        <span
                          className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                            activeTab === "cancelled"
                              ? "bg-teal-100 text-teal-700"
                              : "bg-slate-100 text-slate-500"
                          }`}>
                          {tabCounts.cancelled}
                        </span>
                      )}
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab("mutations")}
                    className={`pb-4 text-sm font-semibold border-b-2 transition-colors ${
                      activeTab === "mutations"
                        ? "border-teal-600 text-teal-600"
                        : "border-transparent text-slate-500 hover:text-slate-700"
                    }`}>
                    Mutasi
                    {tabCounts.mutations !== null && (
                      <span
                        className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                          activeTab === "mutations"
                            ? "bg-teal-100 text-teal-700"
                            : "bg-slate-100 text-slate-500"
                        }`}>
                        {tabCounts.mutations}
                      </span>
                    )}
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex gap-2 text-xs text-slate-500 bg-slate-50/80 px-3 py-1.5 rounded-lg ring-1 ring-slate-200">
                    {shiftLoading ? (
                      <span className="flex items-center gap-1">
                        <Loader2 className="w-3 h-3 animate-spin" /> Memuat
                        shift...
                      </span>
                    ) : activeShift ? (
                      <>
                        <span>
                          Dibuka Oleh:{" "}
                          <strong className="text-slate-700">
                            {activeShift.opened_by_name ||
                              activeShift.opened_by ||
                              "-"}
                          </strong>
                        </span>
                        <span className="w-px h-4 bg-slate-300 mx-1"></span>
                        <span>
                          Buka Kasir:{" "}
                          <strong className="text-slate-700">
                            {formatDateTime(activeShift.opened_at)}
                          </strong>
                        </span>
                      </>
                    ) : (
                      <span className="text-rose-600">Tidak ada shift aktif</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-4">
                {activeTab === "mutations" ? (
                  <ShiftMutationTable
                    activeShift={activeShift}
                    onAddMutation={() => setShowAddMutationModal(true)}
                    onLoadCount={(cnt) => handleTabLoad("mutations", cnt)}
                    refreshKey={tableRefreshKey}
                  />
                ) : (
                  <ShiftTransactionTable
                    shiftId={activeShift?.id}
                    activeTab={activeTab}
                    onViewDetail={(trx) => setSelectedTransaction(trx)}
                    onCancel={(trx) => setCancelTransaction(trx)}
                    onLoad={handleTabLoad}
                    refreshKey={tableRefreshKey}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Modals */}
      {showRecapModal && (
        <ShiftRecapModal
          shiftId={activeShift?.id}
          onClose={() => setShowRecapModal(false)}
        />
      )}

      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
          onRefresh={() => setTableRefreshKey((k) => k + 1)}
        />
      )}

      {cancelTransaction && (
        <CancelTransactionModal
          transaction={cancelTransaction}
          onClose={() => setCancelTransaction(null)}
          onSuccess={handleCancelSuccess}
        />
      )}

      {showCloseShiftModal && (
        <CloseShiftModal
          shiftId={activeShift?.id}
          shift={activeShift}
          onClose={async (closed) => {
            setShowCloseShiftModal(false);
            if (closed) {
              await refreshActiveShift();
              showSuccess("Shift kasir berhasil ditutup");
              navigate("/penjualan");
            }
          }}
        />
      )}

      {showAddMutationModal && (
        <ShiftAddMutationModal
          isOpen={showAddMutationModal}
          onClose={() => setShowAddMutationModal(false)}
          onSuccess={() => setTableRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
